import React, { useEffect, useState, useCallback, memo } from 'react';
import { 
  Box, 
  TextField, 
  Checkbox, 
  Button, 
  IconButton, 
  CircularProgress,
  Typography,
  Tooltip,
  InputAdornment,
  LinearProgress,
  Paper,
  Collapse,
  Divider,
  Alert,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlaylistAdd as PlaylistAddIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useEvent } from '../../hooks/useEvent';

// Componentes estilizados
const TaskContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark' ? '#1b212c' : '#ffffff',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#2f3641' : '#e0e0e0'}`,
  marginBottom: theme.spacing(2),
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
    : '0 1px 3px rgba(0, 0, 0, 0.08)',
}));

const TaskHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.1) : alpha('#000000', 0.05)}`,
}));

const TaskTitle = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
    padding: theme.spacing(0.5, 1),
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.5) },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
}));

const TaskItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.5, 0),
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha('#ffffff', 0.03) 
      : alpha('#000000', 0.02),
  },
}));

const TaskCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginRight: theme.spacing(1),
  '&.Mui-checked': {
    color: theme.palette.success.main,
  },
}));

const TaskInput = styled(TextField)(({ theme, isCompleted }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    fontSize: '0.95rem',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    color: isCompleted 
      ? theme.palette.mode === 'dark' ? alpha('#ffffff', 0.5) : alpha('#000000', 0.5)
      : theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
    textDecoration: isCompleted ? 'line-through' : 'none',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { 
      borderColor: isCompleted
        ? alpha(theme.palette.success.main, 0.3)
        : alpha(theme.palette.primary.main, 0.3) 
    },
    '&.Mui-focused fieldset': { 
      borderColor: isCompleted
        ? theme.palette.success.main
        : theme.palette.primary.main
    },
  },
}));

const AddItemButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  borderRadius: '4px',
  padding: theme.spacing(0.5, 2),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const AddChecklistButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  borderRadius: '4px',
  padding: theme.spacing(1, 2),
  fontWeight: 600,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  padding: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const ProgressLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
}));

const StyledProgress = styled(LinearProgress)(({ theme, value }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.1) : alpha('#000000', 0.05),
  '& .MuiLinearProgress-bar': {
    backgroundColor: value === 100 ? theme.palette.success.main : theme.palette.primary.main,
  },
}));

// Componente de item de checklist (memo para evitar re-renders desnecessários)
const ChecklistItem = memo(({ 
  task, 
  item, 
  loading, 
  onItemChange, 
  onSave, 
  onDeleteItem,
  onChecked,
}) => {
  // Função que envia diretamente para o backend quando o checkbox é alterado
  const handleCheckboxChange = async (e) => {
    const isChecked = e.target.checked;
    await onChecked(task.id, item.id, isChecked);
  };

  return (
    <TaskItem>
      <TaskCheckbox
        checked={item.isCompleted}
        onChange={handleCheckboxChange}
        disabled={loading[`${task.id}-${item.id}`]}
        color={item.isCompleted ? "success" : "primary"}
        size="small"
      />
      <TaskInput
        size="small"
        value={item.description}
        placeholder="Novo item"
        multiline
        isCompleted={!!item?.isCompleted}
        maxRows={6}
        onChange={(e) => onItemChange(task.id, item.id, 'description', e.target.value)}
        onBlur={() => onSave({ taskId: task.id, checklistId: item.id, isTitle: false })}
        disabled={loading[`${task.id}-${item.id}`]}
        InputProps={{
          style: { padding: '8px 12px' },
          endAdornment: loading[`${task.id}-${item.id}`] && (
            <InputAdornment position="end">
              <CircularProgress size={16} color="inherit" />
            </InputAdornment>
          ),
        }}
      />
      <DeleteButton
        size="small"
        onClick={() => onDeleteItem(task.id, item.id)}
        disabled={loading[`${task.id}-${item.id}`]}
      >
        <DeleteIcon fontSize="small" />
      </DeleteButton>
    </TaskItem>
  );
});

// Componente de checklist (memo para evitar re-renders desnecessários)
const Checklist = memo(({ 
  task, 
  idx, 
  loading, 
  onItemChange, 
  onSave, 
  onAddItem, 
  onDeleteItem, 
  onDeleteChecklist,
  onChecked,
}) => {
  // Estado para controlar a expansão/colapso do checklist
  const [expanded, setExpanded] = useState(true);
  
  // Calcula o progresso do checklist
  const completedItems = task.checklists.filter(item => item.isCompleted).length;
  const totalItems = task.checklists.length;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return (
    <TaskContainer>
      <TaskHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Tooltip title={expanded ? "Recolher checklist" : "Expandir checklist"}>
            <IconButton 
              size="small" 
              onClick={() => setExpanded(!expanded)} 
              sx={{ mr: 1 }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
          <TaskTitle
            size="small"
            value={task.title}
            onChange={(e) => onItemChange(task.id, task.id, 'title', e.target.value)}
            onBlur={() => onSave({ taskId: task.id, checklistId: task.id, isTitle: true })}
            placeholder={`Checklist ${idx + 1}`}
            InputProps={{
              endAdornment: loading[`${task.id}-${task.id}`] && (
                <InputAdornment position="end">
                  <CircularProgress size={20} color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            color={progress === 100 ? "success.main" : "primary.main"} 
            sx={{ 
              fontWeight: 600, 
              mr: 2,
              backgroundColor: (theme) => progress === 100 
                ? alpha(theme.palette.success.main, 0.1) 
                : alpha(theme.palette.primary.main, 0.1),
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
            }}
          >
            {Math.round(progress)}%
          </Typography>
          <Tooltip title="Excluir checklist">
            <DeleteButton
              onClick={() => onDeleteChecklist(task.id)}
              disabled={loading[`${task.id}-${task.id}`]}
            >
              <DeleteIcon />
            </DeleteButton>
          </Tooltip>
        </Box>
      </TaskHeader>
      
      <ProgressContainer>
        <ProgressLabel>
          <Typography variant="caption" color="textSecondary" fontWeight={500}>
            {completedItems} de {totalItems} {totalItems === 1 ? 'item' : 'itens'} concluídos
          </Typography>
          {progress === 100 && (
            <Typography variant="caption" color="success.main" fontWeight={600}>
              Concluído
            </Typography>
          )}
        </ProgressLabel>
        <StyledProgress variant="determinate" value={progress} />
      </ProgressContainer>

      <Collapse in={expanded}>
        <Box>
          {task.checklists.map((item) => (
            <ChecklistItem
              key={item.id}
              task={task}
              item={item}
              loading={loading}
              onItemChange={onItemChange}
              onSave={onSave}
              onDeleteItem={onDeleteItem}
              onChecked={onChecked}
            />
          ))}

          <AddItemButton
            size="small"
            variant="text"
            onClick={() => onAddItem(task.id)}
            startIcon={<AddIcon />}
            fullWidth
            disabled={loading[`${task.id}-add`]}
            sx={{ mt: 1 }}
          >
            {loading[`${task.id}-add`] ? (
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            Adicionar item
          </AddItemButton>
        </Box>
      </Collapse>
    </TaskContainer>
  );
});

// Componente principal de tarefas do Kanban
const KanbanCardTasksSection = ({ card }) => {
  const emitter = useEvent();
  const KanbanCardId = card?.id;
  const [tasks, setTasks] = useState([]);
  const [previousTasks, setPreviousTasks] = useState([]);
  const [loading, setLoading] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [error, setError] = useState(null);

  // Sempre busca as subtarefas pela rota — sem cache, sem prop
  const fetchTasks = useCallback(async () => {
    if (!KanbanCardId) return;
    try {
      const { data } = await api.get(`/kanban/card/${KanbanCardId}`);
      const fresh = data?.tasks || [];
      setTasks(fresh);
      setPreviousTasks(fresh);
    } catch (err) {
      console.error("Erro ao buscar checklists:", err);
      setError("Não foi possível carregar os checklists. Tente novamente.");
    } finally {
      setLoadingTasks(false);
    }
  }, [KanbanCardId]);

  useEffect(() => {
    setLoadingTasks(true);
    fetchTasks();
  }, [fetchTasks]);

  // Refetch ao receber eventos relacionados ao card/tarefas/checklists
  useEffect(() => {
    if (!KanbanCardId) return;
    const handler = (event) => {
      if (event?.cardId === KanbanCardId || event?.card?.id === KanbanCardId) {
        fetchTasks();
      }
    };
    emitter.on('kanban-card', handler);
    emitter.on('kanban-task', handler);
    emitter.on('kanban-task-checklist', handler);
    return () => {
      emitter.off('kanban-card', handler);
      emitter.off('kanban-task', handler);
      emitter.off('kanban-task-checklist', handler);
    };
  }, [emitter, KanbanCardId, fetchTasks]);

  // Função para adicionar novo checklist com useCallback
  const handleAddChecklist = useCallback(async () => {
    try {
      setError(null);
      const newTitle = `Checklist ${tasks.length + 1}`;

      await api.post(`/kanban/card/task/${KanbanCardId}`, {
        title: newTitle
      });

      emitter.emit("kanban-task", {
        action: 'update',
        cardId: KanbanCardId,
      });

      await fetchTasks();
    } catch (err) {
      console.error("Erro ao adicionar checklist:", err);
      setError("Não foi possível adicionar o checklist. Tente novamente.");
    }
  }, [KanbanCardId, tasks.length, emitter, fetchTasks]);

  // Função para adicionar item ao checklist com useCallback
  const handleAddItem = useCallback(async (taskId) => {
    try {
      setLoading((prev) => ({ ...prev, [`${taskId}-add`]: true }));
      setError(null);

      await api.post(`/kanban/card/task/checklist/${taskId}`, {
        description: ''
      });

      emitter.emit("kanban-task-checklist", {
        action: 'update',
        taskId,
        cardId: KanbanCardId,
      });

      await fetchTasks();
    } catch (err) {
      console.error("Erro ao adicionar item:", err);
      setError("Não foi possível adicionar o item. Tente novamente.");
    } finally {
      setLoading((prev) => ({ ...prev, [`${taskId}-add`]: false }));
    }
  }, [KanbanCardId, emitter, fetchTasks]);

  // Função otimizada para verificar mudanças nos itens
  const handleItemChange = useCallback((taskId, checklistId, field, value) => {
    try {
      // Se o campo for 'isCompleted', vamos deixar a função handleChecked lidar com isso
      if (field === 'isCompleted') return;
      
      setTasks((prevTasks) => {
        const updatedTasks = prevTasks.map((task) => {
          if (task.id === taskId) {
            if (field === 'title') {
              return { ...task, title: value };
            }

            const updatedChecklists = task.checklists.map((item) => {
              if (item.id === checklistId) {
                return { 
                  ...item, 
                  [field]: value, 
                  saved: field === 'description' ? false : item.saved 
                };
              }
              return item;
            });

            return { ...task, checklists: updatedChecklists };
          }
          return task;
        });

        return updatedTasks;
      });
    } catch (err) {
      console.error('Erro ao atualizar o item:', err);
      setError("Não foi possível atualizar o item. Tente novamente.");
    }
  }, []);

  // Função para salvar alterações
  const handleSave = useCallback(async ({ taskId, checklistId, isTitle }) => {
    setLoading((prev) => ({ ...prev, [`${taskId}-${checklistId}`]: true }));
    setError(null);

    try {
      const taskToUpdate = tasks.find(task => task.id === taskId);

      if (isTitle) {
        await api.put(`/kanban/card/task/${taskId}`, {
          title: taskToUpdate.title
        });
      } else {
        const checklistToUpdate = taskToUpdate.checklists.find(item => item.id === checklistId);

        if (checklistToUpdate) {
          await api.put(`/kanban/card/task/checklist/${checklistId}`, {
            description: checklistToUpdate.description
          });
        }
      }

      emitter.emit(isTitle ? "kanban-task" : "kanban-task-checklist", {
        action: 'update',
        taskId,
        cardId: KanbanCardId,
      });

      await fetchTasks();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setError("Não foi possível salvar as alterações. Tente novamente.");

      // Reverta para o estado anterior em caso de erro
      setTasks(previousTasks);
    } finally {
      setLoading((prev) => ({ ...prev, [`${taskId}-${checklistId}`]: false }));
    }
  }, [tasks, previousTasks, emitter, KanbanCardId, fetchTasks]);

  // Função para marcar/desmarcar item como concluído
  const handleChecked = useCallback(async (taskId, checklistId, value) => {
    try {
      setLoading((prev) => ({ ...prev, [`${taskId}-${checklistId}`]: true }));
      setError(null);

      await api.put(`/kanban/card/task/checklist/${checklistId}`, {
        isCompleted: value,
      });

      emitter.emit("kanban-task-checklist", {
        action: 'update',
        taskId,
        cardId: KanbanCardId,
      });

      await fetchTasks();
    } catch (error) {
      console.error("Erro ao atualizar item:", error);
      setError("Não foi possível atualizar o status do item. Tente novamente.");

      // Reverta para o estado anterior em caso de erro
      setTasks(previousTasks);
    } finally {
      setLoading((prev) => ({ ...prev, [`${taskId}-${checklistId}`]: false }));
    }
  }, [KanbanCardId, emitter, previousTasks, fetchTasks]);

  // Função para excluir item de checklist
  const handleDeleteItem = useCallback(async (taskId, checklistId) => {
    try {
      setLoading((prev) => ({ ...prev, [`${taskId}-${checklistId}`]: true }));
      setError(null);

      await api.delete(`/kanban/card/task/checklist/${checklistId}`);

      emitter.emit("kanban-task-checklist", {
        action: 'update',
        taskId,
        cardId: KanbanCardId,
      });

      await fetchTasks();
    } catch (error) {
      console.error("Erro ao excluir item:", error);
      setError("Não foi possível excluir o item. Tente novamente.");
    } finally {
      setLoading((prev) => ({ ...prev, [`${taskId}-${checklistId}`]: false }));
    }
  }, [KanbanCardId, emitter, fetchTasks]);

  // Função para excluir checklist inteiro
  const handleDeleteChecklist = useCallback(async (taskId) => {
    try {
      setLoading((prev) => ({ ...prev, [`${taskId}-${taskId}`]: true }));
      setError(null);

      await api.delete(`/kanban/card/task/${taskId}`);

      emitter.emit("kanban-task", {
        action: 'update',
        cardId: KanbanCardId,
      });

      await fetchTasks();
    } catch (error) {
      console.error("Erro ao excluir checklist:", error);
      setError("Não foi possível excluir o checklist. Tente novamente.");
    } finally {
      setLoading((prev) => ({ ...prev, [`${taskId}-${taskId}`]: false }));
    }
  }, [KanbanCardId, emitter, fetchTasks]);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 135px)',
        maxHeight: '100%',
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
          borderRadius: 2,
        },
      }}
    >
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {loadingTasks ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {tasks.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              textAlign: 'center',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1b212c' : '#f5f5f5',
              borderRadius: 1,
              p: 4,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Nenhum checklist criado
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Crie checklists para organizar suas tarefas
            </Typography>
            <AddChecklistButton
              onClick={handleAddChecklist}
              startIcon={<PlaylistAddIcon />}
              variant="contained"
            >
              Criar checklist
            </AddChecklistButton>
          </Box>
        ) : (
          <>
            {tasks.map((task, idx) => (
              <Checklist
                key={task.id}
                task={task}
                idx={idx}
                loading={loading}
                onItemChange={handleItemChange}
                onSave={handleSave}
                onAddItem={handleAddItem}
                onDeleteItem={handleDeleteItem}
                onDeleteChecklist={handleDeleteChecklist}
                onChecked={handleChecked}
              />
            ))}

            <AddChecklistButton
              onClick={handleAddChecklist}
              startIcon={<PlaylistAddIcon />}
              disabled={Object.values(loading).some(Boolean)}
              variant="contained"
              fullWidth
            >
              {Object.values(loading).some(val => val === true) && (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              )}
              Adicionar Checklist
            </AddChecklistButton>
          </>
        )}
      </Box>
      )}
    </Box>
  );
};

export default KanbanCardTasksSection;